import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useWarehouseStock } from '@/api/warehouse-sourcing'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { AlertTriangle, FileUp, LayoutTemplate, Plus, RefreshCw, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import type { Quote, QuoteLine } from '../api/types'
import { EquipmentAlternativesDialog } from '../catalog/alternatives'
import { ImportWorkspace } from '../imports/import-workspace'
import { UnfinishedImports } from '../imports/unfinished-imports'
import {
  configurationChoiceGroups,
  configurationQueryOptions,
  defaultChoices,
  equipmentAlternativesQueryOptions,
  equipmentCatalogSearch,
  productsQueryOptions,
  type EquipmentAlternatives,
  type ProductSummary,
  type QuoteOnlyProduct,
} from '../catalog/queries'
import { CatalogDrawer } from '../editor/catalog-drawer'
import { CatalogConfigurationDrawer, ConfigurationDrawer } from '../editor/configuration-drawers'
import { CustomLineDrawer } from '../editor/custom-line-drawer'
import { PricedPlanDrawer } from '../editor/priced-plan-drawer'
import {
  lineCreateBody,
  optimisticLineId,
  optimisticLineInsert,
  optimisticLineRemove,
  optimisticLineUpdate,
  type LineChanges,
  type QuoteEdit,
} from '../editor/edits'
import { useQuoteEditor } from '../editor/use-quote-editor'
import type { WorkflowTabProps } from '../workflow/types'
import { PricingWorksheet, type WorksheetSave } from '../pricing/pricing-tab'
import type { ProtectionPlanOffer } from '../pricing/protection-plans-api'
import type { WorksheetLineActions } from '../pricing/worksheet-equipment'

const zeroTotals = {
  list: '0',
  net: '0',
  sell: '0',
  profit: '0',
  margin_percent: '0',
  map_warning: false,
}

/** A product line shown while its add or replace is saving (demo `optimisticProductLine`). */
function optimisticProductLine(product: ProductSummary, id: string, position: number, source?: QuoteLine): QuoteLine {
  const list = product.price.list_amount ?? product.price.sell_amount ?? '0'
  return {
    id,
    product_id: product.id,
    product_uuid: product.uuid,
    manufacturer_id: product.manufacturer_id,
    manufacturer_uuid: product.manufacturer_uuid,
    parent_line_id: source?.parent_line_id ?? null,
    // A replaced custom item becomes a catalog product line (USA-1338).
    line_type: source?.line_type === 'accessory' ? 'accessory' : 'product',
    is_warranty: false,
    position,
    quantity: source?.quantity ?? '1',
    description: product.title,
    list_amount: list,
    map_amount: product.price.map_amount,
    umrp_amount: product.price.umrp_amount ?? null,
    source_net_amount: product.price.net_amount,
    derived_net_amount: product.price.derived_net_amount ?? null,
    sell_amount: product.price.sell_amount ?? list,
    pricing_adjustments: [],
    discount_chain: product.price.discount_chain ?? null,
    configuration: {},
    alternative_product_ids: [],
    notes: source?.notes ?? null,
    totals: source?.totals ?? zeroTotals,
    primary_asset: product.primary_asset,
    manufacturer: product.manufacturer,
    model: product.model,
    taxonomy_label: product.taxonomy.label ?? 'Uncategorized',
    equipment_description: null,
    spec_text_override: null,
    spec_text: '',
    equipment_details: [],
    configuration_display: {},
    configuration_items: [],
    fulfillment: product.fulfillment,
  }
}

function optimisticCustomLine(values: Record<string, unknown>, id: string, position: number): QuoteLine {
  const list = String(values.list_amount ?? 0)
  return {
    id,
    product_id: null,
    parent_line_id: typeof values.parent_line_id === 'string' ? values.parent_line_id : null,
    line_type: values.line_type === 'service' ? 'service' : 'custom',
    position,
    quantity: String(values.quantity ?? 1),
    description: String(values.description ?? 'Custom line'),
    list_amount: list,
    map_amount: null,
    source_net_amount: String(values.source_net_amount ?? list),
    derived_net_amount: null,
    sell_amount: String(values.sell_amount ?? list),
    pricing_adjustments: [],
    discount_chain: null,
    configuration: {},
    notes: null,
    totals: zeroTotals,
    spec_text: '',
    is_warranty: values.is_warranty === true,
    manufacturer_id: typeof values.manufacturer_id === 'number' ? values.manufacturer_id : null,
  } as QuoteLine
}

type ImportSearch = { import?: unknown; file?: unknown; name?: unknown; documentImport?: unknown }

/**
 * The Equipment stage: build the quote from the live catalog and price it in one worksheet
 * (USA-1140 merged the demo's Equipment and Pricing tabs). `?import=1` opens the import workspace
 * in its place; `&file=<id>&name=` reads a saved quote file and `&documentImport=<id>` reopens a
 * saved import as it was left (USA-1337).
 */
export function EquipmentStage({ quoteId, quote: shellQuote, active = true, onQuoteChange }: WorkflowTabProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as ImportSearch
  const importFile =
    typeof search.file === 'string'
      ? { id: search.file, original_file_name: String(search.name ?? 'Quote file') }
      : undefined
  const documentImportId = typeof search.documentImport === 'string' ? search.documentImport : undefined
  const openImport = (importId?: string) =>
    void navigate({
      to: '/quotes/$quoteId/workflow/$stage',
      params: { quoteId, stage: 'equipment' },
      search: importId ? { import: 1, documentImport: importId } : { import: 1 },
    })
  // The address follows the open import, so a reload reopens it.
  const followImport = (importId: string | null) =>
    void navigate({
      to: '/quotes/$quoteId/workflow/$stage',
      params: { quoteId, stage: 'equipment' },
      search: {
        import: 1,
        ...(importFile ? { file: importFile.id, name: importFile.original_file_name } : {}),
        ...(importId ? { documentImport: importId } : {}),
      },
      replace: true,
    })
  // A file opened from Customer files goes back there; otherwise back to the equipment list.
  const closeImport = () =>
    void navigate({
      to: '/quotes/$quoteId/workflow/$stage',
      params: { quoteId, stage: importFile ? 'customer' : 'equipment' },
    })
  const editor = useQuoteEditor(shellQuote, onQuoteChange)
  const { quote, lineApi, run } = editor
  const [catalogOpen, setCatalogOpen] = useState(false)
  const [catalogProduct, setCatalogProduct] = useState<ProductSummary | null>(null)
  const [pricedPlan, setPricedPlan] = useState<QuoteOnlyProduct | null>(null)
  const [customOpen, setCustomOpen] = useState(false)
  const [customParent, setCustomParent] = useState<QuoteLine | null>(null)
  const [replaceLine, setReplaceLine] = useState<QuoteLine | null>(null)
  const [configureLine, setConfigureLine] = useState<QuoteLine | null>(null)
  const [alternativesLine, setAlternativesLine] = useState<QuoteLine | null>(null)
  const alternativesTrigger = useRef<HTMLButtonElement | null>(null)
  // What our warehouse holds of the quote's equipment (USA-1574).
  const warehouseStock = useWarehouseStock(
    quote.lines.flatMap((line) =>
      line.line_type === 'product' && !line.parent_line_id && line.product_id != null ? [line.product_id] : [],
    ),
  )

  // Warm alternatives, the first catalog page and configurations before they are opened (demo).
  const equipment = shellQuote.lines.filter(
    (line) => line.line_type === 'product' && !line.parent_line_id && line.product_id,
  )
  const equipmentKey = JSON.stringify(
    equipment.map((line) => equipmentAlternativesQueryOptions(editor.api, quoteId, line).queryKey),
  )
  useEffect(() => {
    if (!active) return
    let cancelled = false
    const warm = async () => {
      for (let index = 0; index < equipment.length && !cancelled; index += 2) {
        await Promise.all(
          equipment
            .slice(index, index + 2)
            .map((line) => queryClient.prefetchQuery(equipmentAlternativesQueryOptions(editor.api, quoteId, line))),
        )
      }
      if (cancelled) return
      await queryClient.prefetchQuery(productsQueryOptions(editor.api, equipmentCatalogSearch))
      for (const line of equipment) {
        if (cancelled) return
        await queryClient.prefetchQuery(configurationQueryOptions(editor.api, line.product_id!))
      }
    }
    void warm()
    return () => {
      cancelled = true
    }
    // equipmentKey stands in for the equipment list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, quoteId, equipmentKey, editor.api, queryClient])

  const lineEdit = (lineId: string, changes: LineChanges, key: string, delay = 0): QuoteEdit => ({
    key,
    delay,
    optimistic: (current) => optimisticLineUpdate(current, lineId, changes),
    persist: (current) => lineApi.updateLine(current, lineId, changes),
  })

  const addLine = (line: QuoteLine, body: Record<string, unknown>, position?: number) =>
    run({
      key: `line:${line.id}:add`,
      optimistic: (current) => optimisticLineInsert(current, line, position),
      persist: (current) => lineApi.addLine(current, body),
    })

  const addProduct = (product: ProductSummary, configuration: Record<string, string>) => {
    setCatalogProduct(null)
    const line = {
      ...optimisticProductLine(product, optimisticLineId(), quote.lines.length + 1),
      configuration,
    }
    addLine(line, {
      line_type: 'product',
      product_id: product.id,
      quantity: 1,
      configuration,
    })
  }

  // A plan priced on each quote is its equipment's warranty at the Net and Sell entered (USA-1109).
  const addPricedPlan = (product: QuoteOnlyProduct, parent: QuoteLine, net: string, sell: string) => {
    setPricedPlan(null)
    const position = parent.position + 1
    const line = {
      ...optimisticProductLine(product, optimisticLineId(), position),
      parent_line_id: parent.id,
      is_warranty: true,
      quantity: parent.quantity,
      list_amount: sell,
      source_net_amount: net,
      sell_amount: sell,
    }
    addLine(
      line,
      {
        line_type: 'product',
        product_id: product.id,
        parent_line_id: parent.id,
        is_warranty: true,
        position,
        quantity: parent.quantity,
        source_net_amount: net,
        sell_amount: sell,
      },
      position,
    )
  }

  // A Safeware plan joins its equipment as a warranty at the plan's Net and price (USA-1162).
  const choosePlan = (parent: QuoteLine, offer: ProtectionPlanOffer, current?: QuoteLine) => {
    if (current) {
      run({
        key: `line:${current.id}:remove`,
        optimistic: (value) => optimisticLineRemove(value, current.id),
        persist: (value) => lineApi.removeLine(value, current.id),
      })
    }
    const position = parent.position + 1
    const line: QuoteLine = {
      ...optimisticCustomLine({}, optimisticLineId(), position),
      line_type: 'product',
      product_id: offer.product_id,
      parent_line_id: parent.id,
      is_warranty: true,
      quantity: parent.quantity,
      description: 'Protection Plan',
      manufacturer: 'Safeware',
      model: offer.sku,
      list_amount: offer.price,
      source_net_amount: offer.net,
      sell_amount: offer.price,
      protection_plan: {
        sku: offer.sku,
        term_months: offer.term_months,
        months_added: offer.months_added,
        manufacturer_warranty_months: offer.term_months - offer.months_added,
        warranty_assumed: false,
        terms_url: '',
      },
    }
    addLine(
      line,
      { line_type: 'product', product_id: offer.product_id, parent_line_id: parent.id, is_warranty: true, position },
      position,
    )
  }

  const replaceProduct = (product: ProductSummary) => {
    const line = replaceLine
    setReplaceLine(null)
    if (line) replaceLineProduct(line, product)
  }

  const replaceLineProduct = (line: QuoteLine, product: ProductSummary) => {
    const replacement = optimisticProductLine(product, line.id, line.position, line)
    run({
      key: `line:${line.id}:replace`,
      optimistic: (current) => {
        if (current.lines.find((item) => item.id === line.id)?.product_id === product.id) return current
        // A product's options and accessories go with it; a custom item's own sub-items stay.
        const withoutChildren = current.lines.filter(
          (item) => line.line_type === 'custom' || item.id === line.id || !isDescendant(current, item, line.id),
        )
        return optimisticLineUpdate({ ...current, lines: withoutChildren }, line.id, replacement)
      },
      persist: (current) => lineApi.replaceLine(current, line.id, product.id),
    })
  }

  // A version row (Gas type: Natural gas | Liquid propane) swaps the line to that sibling SKU,
  // keeping its quantity; its choices then start from the new product's defaults (USA-1392).
  const switchVersion = (line: QuoteLine, product: ProductSummary) => {
    replaceLineProduct(line, product)
    run({
      key: `line:${line.id}:version-defaults`,
      optimistic: (current) => current,
      persist: async (current) => {
        if (current.lines.find((item) => item.id === line.id)?.product_id !== product.id) return current
        const configuration = await queryClient.fetchQuery(configurationQueryOptions(editor.api, product.id))
        const defaults = defaultChoices(configurationChoiceGroups(configuration))
        if (!Object.keys(defaults).length) return current
        return lineApi.updateLine(current, line.id, { configuration: defaults })
      },
    })
  }

  // Pricing edits ride the same queue as equipment edits, so they save in order (USA-1140).
  const save: WorksheetSave = (key, persist) =>
    new Promise((resolve) => {
      let attempts = 0
      run({
        key,
        optimistic: (current) => current,
        persist: async (current) => {
          attempts += 1
          try {
            const next = await persist(current)
            resolve({ saved: true })
            return next
          } catch (error) {
            // A stale revision is replayed once by the queue; only the final failure counts.
            const stale = typeof error === 'object' && error !== null && 'status' in error && error.status === 412
            if (!stale || attempts > 1) resolve({ saved: false, error })
            throw error
          }
        },
      })
    })

  const actions: WorksheetLineActions = {
    onChoosePlan: choosePlan,
    onMove: (line, position) => run(lineEdit(line.id, { position }, `line:${line.id}:position`)),
    onManufacturer: (line, manufacturerId) =>
      run(lineEdit(line.id, { manufacturer_id: manufacturerId }, `line:${line.id}:manufacturer`)),
    warehouseStock: warehouseStock.data,
    onWarehouseSource: (line, source, label) =>
      run({
        key: `line:${line.id}:warehouse-source`,
        optimistic: (current) =>
          optimisticLineUpdate(current, line.id, {
            warehouse_source:
              source && label
                ? { ...source, label, only_from_warehouse: line.warehouse_source?.only_from_warehouse ?? false }
                : null,
          }),
        persist: (current) => lineApi.updateLine(current, line.id, { warehouse_source: source }),
      }),
    onDuplicate: (line) => {
      const duplicate = { ...line, id: optimisticLineId() }
      addLine(duplicate, lineCreateBody(line, line.position + 1), line.position + 1)
    },
    onRemove: (line) =>
      run({
        key: `line:${line.id}:remove`,
        optimistic: (current) => optimisticLineRemove(current, line.id),
        persist: (current) => lineApi.removeLine(current, line.id),
      }),
    onConfigure: setConfigureLine,
    onAlternatives: (line, trigger) => {
      alternativesTrigger.current = trigger
      setAlternativesLine(line)
    },
    onReplace: setReplaceLine,
    onAddCustomChild: (line) => {
      setCustomParent(line)
      setCustomOpen(true)
    },
  }

  const lastFailure = Object.values(editor.failures).at(-1)

  if (active && search.import) {
    return (
      <ImportWorkspace
        initialImportId={documentImportId}
        initialQuoteFile={importFile}
        key={importFile?.id}
        onClose={closeImport}
        onImportChange={followImport}
        onQuoteChange={onQuoteChange}
        quote={shellQuote}
      />
    )
  }

  return (
    <div className="grid min-w-0 gap-3" data-stage="equipment">
      <TooltipProvider>
        <div className="flex flex-wrap justify-end gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0}>
                <Button disabled variant="outline">
                  <LayoutTemplate />
                  Start from template
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>No templates are available yet</TooltipContent>
          </Tooltip>
          <Button onClick={() => openImport()} variant="outline">
            <FileUp />
            Import file
          </Button>
          <Button onClick={() => setCatalogOpen(true)} variant="outline">
            <Search />
            Catalog
          </Button>
          <Button
            onClick={() => {
              setCustomParent(null)
              setCustomOpen(true)
            }}
            variant="outline"
          >
            <Plus />
            Custom line
          </Button>
        </div>
      </TooltipProvider>

      <UnfinishedImports onResume={openImport} quoteId={quoteId} />

      {editor.error ? (
        <div
          className={cn(
            'flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 text-sm',
            editor.conflict
              ? 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100'
              : 'border-destructive/40 bg-destructive/5 text-destructive',
          )}
          role="alert"
        >
          <AlertTriangle aria-hidden="true" className="size-4 shrink-0" />
          <span className="min-w-0 flex-1">{editor.error}</span>
          {editor.conflict ? (
            <LoadingButton
              loading={editor.reloading}
              loadingText="Reloading..."
              onClick={() => void editor.reload()}
              size="sm"
              variant="outline"
            >
              <RefreshCw />
              Reload latest
            </LoadingButton>
          ) : lastFailure ? (
            <Button onClick={() => run(lastFailure.edit)} size="sm" variant="outline">
              <RefreshCw />
              Retry edit
            </Button>
          ) : (
            <Button aria-label="Dismiss error" onClick={editor.dismissError} size="icon" variant="ghost">
              <X />
            </Button>
          )}
        </div>
      ) : null}

      <PricingWorksheet
        actions={actions}
        active={active}
        onReload={editor.reload}
        quote={quote}
        quoteId={quoteId}
        save={save}
        saving={editor.saving}
      />

      <CatalogDrawer
        busy={editor.saving}
        onOpenChange={setCatalogOpen}
        onSelect={(product) => {
          setCatalogOpen(false)
          if ('priced_on_each_quote' in product && product.priced_on_each_quote) setPricedPlan(product)
          else setCatalogProduct(product)
        }}
        open={catalogOpen}
      />
      {pricedPlan ? (
        <PricedPlanDrawer
          busy={editor.saving}
          equipment={quote.lines.filter(
            (line) => line.line_type === 'product' && !line.parent_line_id && line.product_id,
          )}
          key={pricedPlan.id}
          onAdd={({ parent, net, sell }) => addPricedPlan(pricedPlan, parent, net, sell)}
          onCancel={() => {
            setPricedPlan(null)
            setCatalogOpen(true)
          }}
          product={pricedPlan}
        />
      ) : null}
      {catalogProduct ? (
        <CatalogConfigurationDrawer
          key={catalogProduct.id}
          onAdd={(configuration) => addProduct(catalogProduct, configuration)}
          onCancel={() => {
            setCatalogProduct(null)
            setCatalogOpen(true)
          }}
          product={catalogProduct}
        />
      ) : null}
      {alternativesLine ? (
        <EquipmentAlternativesDialog
          busy={editor.alternativesSaving}
          error={Boolean(editor.failures[`line:${alternativesLine.id}:alternatives`])}
          key={alternativesLine.id}
          line={alternativesLine}
          onClose={() => setAlternativesLine(null)}
          onSave={(productIds) => {
            const line = alternativesLine
            editor.setAlternativesSaving(true)
            run({
              key: `line:${line.id}:alternatives`,
              kind: 'alternatives',
              optimistic: (current) => current,
              persist: async (current) => {
                const next = await lineApi.setAlternatives(current, line.id, productIds)
                const saved = next.lines.find((item) => item.id === line.id)
                const cached = queryClient.getQueryData<EquipmentAlternatives>(
                  equipmentAlternativesQueryOptions(editor.api, quoteId, line).queryKey,
                )
                if (saved && cached?.current.product_id === saved.product_id) {
                  queryClient.setQueryData(equipmentAlternativesQueryOptions(editor.api, quoteId, saved).queryKey, {
                    ...cached,
                    selected_product_ids: productIds,
                  })
                }
                editor.setAlternativesSaving(false)
                setAlternativesLine(null)
                return next
              },
            })
          }}
          quoteId={quoteId}
          returnFocus={() => {
            const trigger = alternativesTrigger.current
            const visible = trigger?.getClientRects().length
              ? trigger
              : Array.from(document.querySelectorAll<HTMLButtonElement>('button[aria-haspopup="dialog"]')).find(
                  (button) =>
                    button.getAttribute('aria-label') === trigger?.getAttribute('aria-label') &&
                    button.getClientRects().length,
                )
            visible?.focus()
          }}
        />
      ) : null}
      <CatalogDrawer
        busy={false}
        onOpenChange={(open) => {
          if (!open) setReplaceLine(null)
        }}
        onSelect={replaceProduct}
        open={Boolean(replaceLine)}
        title={`Replace ${replaceLine?.model || replaceLine?.description || 'product'}`}
      />
      <CustomLineDrawer
        busy={false}
        onOpenChange={setCustomOpen}
        onSave={(values) => {
          setCustomOpen(false)
          const parent = customParent
          setCustomParent(null)
          const position = parent ? parent.position + 1 : quote.lines.length + 1
          const body = parent ? { ...values, parent_line_id: parent.id, position } : values
          addLine(optimisticCustomLine(body, optimisticLineId(), position), body, position)
        }}
        open={customOpen}
        parentName={customParent?.model || customParent?.description}
      />
      <ConfigurationDrawer
        busy={false}
        line={configureLine}
        onAddAccessory={(parent, product) => {
          const position = parent.position + 1
          const line = {
            ...optimisticProductLine(product, optimisticLineId(), position),
            parent_line_id: parent.id,
            line_type: 'accessory' as const,
          }
          addLine(
            line,
            {
              product_id: product.id,
              parent_line_id: parent.id,
              line_type: 'accessory',
              position,
              quantity: 1,
            },
            position,
          )
        }}
        onOpenChange={(open) => {
          if (!open) setConfigureLine(null)
        }}
        onSave={(line, configuration) => run(lineEdit(line.id, { configuration }, `line:${line.id}:configuration`))}
        onSwitchVersion={switchVersion}
      />
    </div>
  )
}

function isDescendant(quote: Quote, line: QuoteLine, ancestorId: string) {
  let parentId = line.parent_line_id
  const seen = new Set<string>()
  while (parentId && !seen.has(parentId)) {
    if (parentId === ancestorId) return true
    seen.add(parentId)
    parentId = quote.lines.find((item) => item.id === parentId)?.parent_line_id ?? null
  }
  return false
}
